import { useCallback, useEffect, useRef } from 'react';
import { DID_FETCH_PROJECT_TRANSACTIONS, DID_GET_TRANSACTION } from '../../constants';
import { hasWindow } from '../../utils';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';

const WEB_SOCKET_READY_STATES = {
    CONNECTING: 0,
    OPEN: 1,
    CLOSING: 2,
    CLOSED: 3,
};

const getPath = (projectIds = [], eventIds = [], tags = []) => {
    const query = queryString.stringify({
        tags,
        eventIds,
        projectIds,
    });
    return `/api/stream/transaction?${query}`;
};

const useLatestTransactionsStreamListener = (projectIds, eventIds, tags) => {
    // Use stable references for the parameters, because otherwise they make new websockets on each render
    const dispatch = useDispatch();
    const webSocketRef = useRef();
    const baseUrl = useSelector((state) => state.baseURL);
    const isBrowser = hasWindow();

    const newWebSocket = useCallback(() => {
        if (
            webSocketRef?.current?.readyState === WEB_SOCKET_READY_STATES.OPEN ||
            webSocketRef?.current?.readyState === WEB_SOCKET_READY_STATES.CONNECTING
        ) {
            return;
        }

        const url = baseUrl
            .replace('https:', 'wss:')
            .replace('http:', 'ws:')
            .concat(getPath(projectIds, eventIds, tags));

        try {
            const webSocket = new WebSocket(url);
            webSocket.shouldReconnect = true;

            webSocket.addEventListener('message', (evt) => {
                if (projectIds?.length === 1) {
                    dispatch({
                        type: DID_FETCH_PROJECT_TRANSACTIONS,
                        data: { transactions: [JSON.parse(evt.data)], projectId: projectIds[0] },
                    });
                }
                dispatch({ type: DID_GET_TRANSACTION, data: JSON.parse(evt.data) });
            });

            webSocket.onclose = () => {
                if (webSocket.shouldReconnect) {
                    setTimeout(newWebSocket, 10000);
                }
            };

            webSocketRef.current = webSocket;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    }, [dispatch, baseUrl]);

    useEffect(() => {
        if (isBrowser && 'WebSocket' in window) {
            const closeWebSocket = () => {
                if (webSocketRef?.current) {
                    webSocketRef.current.shouldReconnect = false;
                }
                if (
                    webSocketRef?.current?.readyState !== WEB_SOCKET_READY_STATES.CLOSING &&
                    webSocketRef?.current?.readyState !== WEB_SOCKET_READY_STATES.CLOSED
                ) {
                    webSocketRef?.current?.close();
                }
            };

            newWebSocket();

            window.addEventListener('online', newWebSocket);
            window.addEventListener('offline', closeWebSocket);

            return () => {
                window.removeEventListener('online', newWebSocket);
                window.removeEventListener('offline', closeWebSocket);
                closeWebSocket();
            };
        }
    }, [dispatch, isBrowser, newWebSocket, projectIds, eventIds, tags]);
};

export default useLatestTransactionsStreamListener;
